import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BrandMark } from "@/components/Brand";
import { OtpStep } from "@/components/OtpStep";
import { GoogleButton, OrDivider } from "@/components/GoogleButton";
import { startGoogleLogin } from "@/pages/AuthCallback";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const VIA = [
  { key: "primary", label: "My email" },
  { key: "backup_email", label: "Backup email" },
  { key: "backup_phone", label: "Backup phone" },
];

export default function AdminLogin() {
  const { admin, signIn } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [via, setVia] = useState("primary");
  const [sentTo, setSentTo] = useState("");
  const [busy, setBusy] = useState(false);

  const request = async () => {
    const { data } = await api.post("/admin/auth/request", { email, via });
    setSentTo(data.sent_to);
    toast.success(data.mocked ? `Code sent by SMS to ${data.sent_to} (SMS mocked)` : `Code sent to ${data.sent_to}`);
  };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try { await request(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const verify = async (code: string) => {
    try {
      const { data } = await api.post("/admin/auth/verify", { email, code });
      await signIn("admin", data.token);
      nav("/admin");
    } catch (err) { toast.error(errMsg(err)); }
  };

  if (admin?.user) return <Navigate to="/admin" replace />;
  return (
    <div className="grid min-h-screen place-items-center bg-background p-6">
      <div className="hard-shadow w-full max-w-sm space-y-6 rounded-sm border bg-white p-8" data-testid="admin-login-page">
        <BrandMark />
        <div>
          <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-primary"><ShieldCheck className="h-4 w-4" /> Platform admin</p>
          <h1 className="mt-1 text-2xl font-semibold">Staff sign in</h1>
          <p className="mt-1 text-sm text-muted-foreground">For ExpoEventHub company staff only.</p>
        </div>
        {!sentTo ? (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2"><Label>Staff email</Label>
              <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" /></div>
            <div className="space-y-2">
              <Label>Send my code to</Label>
              <div className="grid grid-cols-3 gap-1 rounded-sm border p-1">
                {VIA.map((v) => (
                  <button type="button" key={v.key} onClick={() => setVia(v.key)} data-testid={`admin-via-${v.key}`}
                    className={`rounded-sm px-2 py-1.5 text-xs font-semibold ${via === v.key ? "bg-primary text-white" : "text-muted-foreground hover:bg-muted"}`}>{v.label}</button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Lost access to your email? Use the backup option set by your admin.</p>
            </div>
            <Button type="submit" disabled={busy} className="hard-shadow h-11 w-full rounded-sm" data-testid="admin-send-code-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send code"}
            </Button>
            <OrDivider />
            <GoogleButton onClick={() => startGoogleLogin("admin", "/admin")} testid="admin-google-button" />
          </form>
        ) : (
          <OtpStep email={sentTo} onVerify={verify} onResend={() => request().catch((e) => toast.error(errMsg(e)))} onBack={() => setSentTo("")} />
        )}
      </div>
    </div>
  );
}
